import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Star, Trophy, Flame, CheckCircle, Target } from "lucide-react";
import { getLevel } from "./AchievementBadges";

interface Props {
  totalStars: number;
  totalScore: number;
  completedToday: number;
  totalActivities: number;
  streak?: number;
}

const DAILY_GOAL = 3;

export default function ActivitySummaryPanel({ totalStars, totalScore, completedToday, totalActivities, streak = 0 }: Props) {
  const level = getLevel(totalScore);
  const dailyProgress = Math.min((completedToday / DAILY_GOAL) * 100, 100);

  return (
    <div className="space-y-3">
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-white">
          <CardContent className="p-4 text-center">
            <Star className="h-7 w-7 mx-auto text-yellow-400 fill-yellow-400" />
            <p className="text-2xl font-bold text-foreground mt-1">{totalStars}</p>
            <p className="text-xs text-muted-foreground">نجوم</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4 text-center">
            <Trophy className="h-7 w-7 mx-auto text-blue-500" />
            <p className="text-2xl font-bold text-foreground mt-1">{totalScore}</p>
            <p className="text-xs text-muted-foreground">نقاط</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-white">
          <CardContent className="p-4 text-center">
            <Flame className="h-7 w-7 mx-auto text-orange-500" />
            <p className="text-2xl font-bold text-foreground mt-1">{streak}</p>
            <p className="text-xs text-muted-foreground">أيام متتالية 🔥</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-7 w-7 mx-auto text-green-500" />
            <p className="text-2xl font-bold text-foreground mt-1">{totalActivities}</p>
            <p className="text-xs text-muted-foreground">إجمالي</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-4 text-center">
            <span className="text-2xl block">{level.emoji}</span>
            <p className="text-sm font-bold text-foreground mt-1">{level.label}</p>
            <p className="text-xs text-muted-foreground">المستوى</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily goal progress */}
      <Card className="border-muted">
        <CardContent className="p-3 flex items-center gap-3">
          <Target className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">الهدف اليومي</span>
              <span className="font-medium text-foreground">{completedToday} / {DAILY_GOAL}</span>
            </div>
            <Progress value={dailyProgress} className="h-2" />
          </div>
          {completedToday >= DAILY_GOAL && <span className="text-lg">🎉</span>}
        </CardContent>
      </Card>
    </div>
  );
}
